import classNames from "classnames";
import { setTrackingId } from "client/Misc/utils";
import {
	PropsWithChildren,
	ReactElement,
	Ref,
	forwardRef
} from "react";
import styles from "./AddressBookPageLayout.module.less";

export type AddressBookPageLayoutProp = {
	trackingId: string;
};

const AddressBookPageLayoutInternal = forwardRef<HTMLDivElement, PropsWithChildren<AddressBookPageLayoutProp>>(
	(
		props: PropsWithChildren<AddressBookPageLayoutProp>,
		ref: Ref<HTMLDivElement>
	): ReactElement => {
		const {
			children,
			trackingId
		} = props;

		return (
			<div
				{...setTrackingId(trackingId)}
				className={styles.root}
				ref={ref}
			>
				{children}
			</div>
		);
	}
);

AddressBookPageLayoutInternal.displayName="AddressBookPageLayout";

const Header = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div className={styles.header}>
			{children}
		</div>
	);
};

Header.displayName= "AddressBookPageLayout.Header";

const SearchAndMobileFilter = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div className={styles.searchAndMobileFilter}>
			{children}
		</div>
	);
};

SearchAndMobileFilter.displayName= "AddressBookPageLayout.SearchAndMobileFilter";

type ContentProps = {
	hasLeftPadding: boolean;
	hasRightPadding: boolean;
};

const Content = (props: PropsWithChildren<ContentProps>): ReactElement => {
	const {
		hasLeftPadding,
		hasRightPadding,
		children
	} = props;

	return (
		<div
			className={classNames(
				styles.content,
				{
					[styles.content_hasNoLeftPadding]: !hasLeftPadding,
					[styles.content_hasNoRightPadding]: !hasRightPadding
				}
			)}
		>
			{children}
		</div>
	);
};

Content.displayName= "AddressBookPageLayout.Content";

export type ItemsPanelProps = {
	hasBorder: boolean;
	shouldReserveSpaceForPlusButton: boolean;
};

const ItemsPanel = (props: PropsWithChildren<ItemsPanelProps>): ReactElement => {
	const {
		hasBorder,
		shouldReserveSpaceForPlusButton,
		children
	} = props;

	return (
		<div
			className={classNames(
				styles.itemsPanel,
				{ [styles.itemsPanel_bordered]: hasBorder }
			)}
		>
			{children}

			{shouldReserveSpaceForPlusButton && (
				<div className="preserve-space-for-mobile-plus-button" />
			)}
		</div>
	);
};
ItemsPanel.displayName = "ItemsPanel";

const DetailsPanel = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div className={styles.detailsPanel}>
			{children}
		</div>
	);
};
DetailsPanel.displayName = "DetailsPanel";

type AddressBookPageLayoutWithSubComponents = typeof AddressBookPageLayoutInternal & {
	Header: typeof Header;
	SearchAndMobileFilter: typeof SearchAndMobileFilter;
	Content: typeof Content;
	ItemsPanel: typeof ItemsPanel;
	DetailsPanel: typeof DetailsPanel;
};

export const AddressBookPageLayout = AddressBookPageLayoutInternal as unknown as AddressBookPageLayoutWithSubComponents;
AddressBookPageLayout.Header = Header;
AddressBookPageLayout.SearchAndMobileFilter = SearchAndMobileFilter;
AddressBookPageLayout.Content = Content;
AddressBookPageLayout.ItemsPanel = ItemsPanel;
AddressBookPageLayout.DetailsPanel = DetailsPanel;
